import isTizen from '@accedo/xdk-device-samsung-tizen/esm/detection'
import isVizio from '@accedo/xdk-device-vizio-smartcast-contrib/esm/detection'
import { Lightning, Router } from '@lightningjs/sdk'

import { Registry } from '@lightningjs/sdk'
import Button from '../../components/button/button'
import { NativeInputBox } from '../../components/nativeInputBox/nativeInputBox'
import { QwertyKeyboard } from '../../components/QwertyKeyboard/QwertyKeyboard'
import {
  getColor,
  grey200,
  grey300,
  grey50,
  grey960,
  transparent,
  white,
} from '../../constants/colors'
import { UniNeueBold, UniNeueHeavy, UniNeueRegular } from '../../constants/fonts'
import { LOGIN_ICON_HEIGHT, LOGIN_ICON_WIDTH } from '../../constants/general'
import { Images } from '../../constants/paths'
import { BASE_ROUTES, FIRST_LANDING, HOME, LOGIN } from '../../constants/routes'
import { AppConfig } from '../../services/appConfig'
import { Flo } from '../../types/flo-auth-api'
import {
  hasActiveSubscription,
  isUserSignedIn,
  storeUserData,
  updateUserSubscriptionData,
} from '../../utils/authentication'
import { getText } from '../../utils/dictionary'
import { getAsset, getIcon } from '../../utils/image'
import { validateEmail } from '../../utils/misc'
import {
  captureClick,
  captureSessionData,
  SEGMENT_TRACK_EVENTS,
} from '../../utils/segmentAnalytics'
import PageHandler from '../handlers/pageHandler'
import ProductBullet from './productBullet'
import ProductsList from './productsList'

enum SignUpStates {
  EnterEmail = 'EnterEmail',
  EnterPassword = 'EnterPassword',
  SelectProduct = 'SelectProduct',
  Keyboard = 'Keyboard',
}

const SUBSCRIPTION_PURCHASE_TIMEOUT_DELAY = 30000

export default class SignUp extends PageHandler {
  private _currentButton: Lightning.Component
  private _lastState: string
  private _email: string
  private _password: string
  private _continueStep1BtnEnabled: boolean
  private _nativeProducts: ProductList
  private _productsMap: { [key: string]: IProduct } = {}
  private _commonProducts: Flo.Offers.OffersList
  private _contextData: SubscriptionContextData
  private _fromSettings: boolean
  private _isPurchasingSubscription: boolean
  private _subscriptionPurchaseTimeoutId: number

  static _template() {
    return {
      w: 1920,
      h: 1080,
      Container: {
        w: (w) => w,
        h: (h) => h,
        flex: {
          direction: 'column',
          alignItems: 'center',
        },
        EmailContainer: {
          w: 1920,
          h: 1080,
          Logo: {
            w: 246,
            h: 28,
            y: 48,
            x: 48,
            texture: Lightning.Tools.getSvgTexture(getAsset(Images.LOGO_LARGE), 246, 28),
          },
          EmailInnerContainer: {
            y: 168,
            w: 1920,
            h: 912,
            rect: true,
            texture: Lightning.Tools.getSvgTexture(getAsset(Images.SIGN_UP_EMAIL_BG), 1920, 912),
            flex: {
              direction: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
            },
            Title: {
              flexItem: {
                marginTop: 48,
              },
              w: 668,
              text: {
                textAlign: 'center',
                text: getText('enterYourEmailAddress'),
                textColor: getColor(white),
                fontSize: 48,
                lineHeight: 56,
                letterSpacing: 0.48,
                fontFace: UniNeueHeavy,
              },
            },
            EmailInputField: {
              flexItem: {
                marginTop: 32,
              },
              type: NativeInputBox,
              w: 668,
              h: 84,
              placeholder: 'Email',
              fontSize: 32,
              textColor: getColor(grey50),
              placeholderColor: getColor(grey300),
              color: getColor(grey960),
              fontFace: UniNeueRegular,
              iconImage: Lightning.Tools.getSvgTexture(
                getIcon('icon-envelope'),
                LOGIN_ICON_WIDTH,
                LOGIN_ICON_HEIGHT,
              ),
              iconColor: getColor(grey50),
              onCompleteSignal: 'onEmailComplete',
              onTextChangeSignal: 'onEmailChange',
              signals: {
                onEmailComplete: 'onEmailComplete',
                onEmailChange: 'onEmailChange',
              },
            },
            ContinueStep1Button: {
              flexItem: {
                marginTop: 32,
              },
              w: 668,
              type: Button,
              label: getText('continue'),
              isEnabled: false,
            },
          },
        },
        PasswordContainer: {
          w: 1920,
          h: 1080,
          Logo: {
            w: 246,
            h: 28,
            y: 48,
            x: 48,
            texture: Lightning.Tools.getSvgTexture(getAsset(Images.LOGO_LARGE), 246, 28),
          },
          visible: false,
          PasswordInnerContainer: {
            y: 168,
            w: 1920,
            h: 912,
            rect: true,
            texture: Lightning.Tools.getSvgTexture(getAsset(Images.SIGN_UP_PASSWORD_BG), 1920, 912),
            flex: {
              direction: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
            },
            Title: {
              flexItem: {
                marginTop: 48,
              },
              w: 668,
              text: {
                textAlign: 'center',
                text: getText('enterYourPassword'),
                textColor: getColor(white),
                fontSize: 48,
                lineHeight: 56,
                letterSpacing: 0.48,
                fontFace: UniNeueHeavy,
              },
            },
            PasswordInputField: {
              flexItem: {
                marginTop: 32,
              },
              type: NativeInputBox,
              w: 668,
              h: 84,
              placeholder: 'Password',
              fontSize: 32,
              textColor: getColor(grey50),
              placeholderColor: getColor(grey300),
              color: getColor(grey960),
              fontFace: UniNeueRegular,
              isPassword: true,
              iconImage: Lightning.Tools.getSvgTexture(
                getIcon('icon-lock'),
                LOGIN_ICON_WIDTH,
                LOGIN_ICON_HEIGHT,
              ),
              iconColor: getColor(grey50),
              onCompleteSignal: 'onPasswordComplete',
              signals: {
                onPasswordComplete: 'onPasswordComplete',
              },
            },
            ContinueStep2Button: {
              flexItem: {
                marginTop: 32,
              },
              w: 668,
              type: Button,
              label: getText('continue'),
            },
            TogglePasswordButton: {
              flexItem: {
                marginTop: 32,
              },
              w: 668,
              type: Button,
              label: getText('showPassword'),
            },
          },
        },
        ProductsContainer: {
          visible: false,
          flex: {
            direction: 'row',
            //paddingLeft: LEFT_SAFE_ZONE_MARGIN,
          },
          LeftContainer: {
            flex: {
              direction: 'col',
              alignItems: 'center',
            },
            w: 876,
            h: 1080,
            rect: true,
            color: transparent,
            Title: {
              flexItem: {
                marginTop: 48,
              },
              w: 668,
              text: {
                text: getText('signUpUnlockContent'),
                textColor: getColor(white),
                fontSize: 64,
                lineHeight: 72,
                letterSpacing: 0.64,
                fontFace: UniNeueHeavy,
              },
            },
            SubTitle: {
              flexItem: {
                marginTop: 48,
              },
              w: 668,
              text: {
                textAlign: 'center',
                text: getText('signUpPleaseChoose'),
                textColor: getColor(grey200),
                fontSize: 32,
                lineHeight: 45,
                fontFace: UniNeueRegular,
              },
            },
            ProductsList: {
              y: 80,
              h: 600,
              type: ProductsList,
            },
          },
          RightContainer: {
            w: 1044,
            h: 1080,
            rect: true,
            color: transparent,
            Background: {
              y: 0,
              texture: Lightning.Tools.getSvgTexture(
                getAsset(Images.SIGN_UP_PRODUCT_INFO_BG),
                1044,
                1080,
              ),

              flex: {
                direction: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
              },

              ProductInfoHeader: {
                flexItem: {
                  marginLeft: 64,
                },
                w: 668,
                text: {
                  text: '',
                  textColor: getColor(white),
                  fontSize: 64,
                  lineHeight: 56,
                  letterSpacing: 0.48,
                  fontFace: UniNeueBold,
                },
              },
              ProductInfoBullets: {
                flexItem: {
                  marginTop: 40,
                  marginLeft: 64,
                },
                flex: {
                  direction: 'column',
                  alignItems: 'flex-start',
                },
              },
            },
          },
        },
      },
      Keyboard: {
        x: 540,
        y: 615,
        visible: isVizio,
        type: QwertyKeyboard,
      },
    }
  }

  get pageText() {
    return 'Sign Up'
  }

  get keyboard() {
    return this.tag('Keyboard')
  }

  get emailInputField() {
    return this.tag('EmailInputField') as NativeInputBox
  }

  get continueStep1Button() {
    return this.tag('ContinueStep1Button') as Button
  }

  get passwordInputField() {
    return this.tag('PasswordInputField') as NativeInputBox
  }

  get continueStep2Button() {
    return this.tag('ContinueStep2Button') as Button
  }

  get togglePasswordButton() {
    return this.tag('TogglePasswordButton') as Button
  }

  get emailContainer() {
    return this.tag('EmailContainer') as Lightning.Component
  }

  get passwordContainer() {
    return this.tag('PasswordContainer') as Lightning.Component
  }

  get productsContainer() {
    return this.tag('ProductsContainer') as Lightning.Component
  }

  get productsList() {
    return this.tag('ProductsList') as ProductsList
  }

  get productInfoBullets() {
    return this.tag('ProductInfoBullets')
  }

  set headerText(v: string) {
    this.tag('ProductInfoHeader').text.text = v
  }

  set params(v: { contextData: SubscriptionContextData; fromSettings?: boolean }) {
    this._contextData = v?.contextData
    this._fromSettings = v?.fromSettings
  }

  _init() {
    if (isUserSignedIn()) {
      this._lastState = SignUpStates.SelectProduct
    } else {
      this._lastState = SignUpStates.EnterEmail
    }

    this._setState(this._lastState)
    this._currentButton = this.emailInputField
    this._isPurchasingSubscription = false
  }

  _active() {
    this.setLoadingAnimation(false)
    this.setModal(false)
  }

  _disable() {
    this._isPurchasingSubscription = false
    Registry.clearTimeout(this._subscriptionPurchaseTimeoutId)
    this._subscriptionPurchaseTimeoutId = null
  }

  static _states() {
    return [
      class Keyboard extends this {
        _getFocused() {
          return this.keyboard
        }
      },
      class FocusOnParent extends this {
        _getFocused() {
          return null
        }

        _handleDown() {
          this._setState(this._lastState)
        }
      },

      class EnterEmail extends this {
        get sectionText() {
          return getText('enterYourEmailAddress')
        }

        _getFocused() {
          return this._currentButton
        }

        $enter() {
          this._lastState = this._getState()
          this.showEmailContainer()
          this._currentButton = this.emailInputField
        }

        _handleEnter() {
          if (this._currentButton === this.continueStep1Button) {
            this._email = this.emailInputField.getText()
            this._setState('EnterPassword')
          } else if (this._currentButton === this.emailInputField) {
            this.emailInputField.startInput()
          }
        }

        _handleBack() {
          Router.back()
        }

        _handleDown() {
          if (this._currentButton === this.emailInputField && this._continueStep1BtnEnabled) {
            this._currentButton = this.continueStep1Button
          }
        }

        _handleUp() {
          if (this._currentButton === this.continueStep1Button) {
            this._currentButton = this.emailInputField
          } else if (this._currentButton === this.emailInputField) {
            this.fireAncestors('$returnFocusToParent')
            this._setState(SignUpStates.EnterEmail)
          }
        }
      },
      class EnterPassword extends this {
        get sectionText() {
          return getText('enterYourPassword')
        }

        _getFocused() {
          return this._currentButton
        }

        $enter() {
          this._lastState = this._getState()
          this.showPasswordContainer()
          this._currentButton = this.passwordInputField
        }

        _handleEnter() {
          if (this._currentButton === this.passwordInputField) {
            this.passwordInputField.startInput()
          } else if (this._currentButton === this.continueStep2Button) {
            this._password = this.passwordInputField.getText()
            this.registerUser()
          } else if (this._currentButton === this.togglePasswordButton) {
            this.passwordInputField.toggleIsPassword()
            this.togglePasswordButton.label = getText(
              this.passwordInputField.isPassword ? 'showPassword' : 'hidePassword',
            )
          }
        }

        _handleBack() {
          this._setState(SignUpStates.EnterEmail)
        }

        _handleDown() {
          if (this._currentButton === this.passwordInputField) {
            this._currentButton = this.continueStep2Button
          } else if (this._currentButton === this.continueStep2Button) {
            this._currentButton = this.togglePasswordButton
          }
        }

        _handleUp() {
          if (this._currentButton === this.continueStep2Button) {
            this._currentButton = this.passwordInputField
          } else if (this._currentButton === this.togglePasswordButton) {
            this._currentButton = this.continueStep2Button
          }
        }
      },

      class SelectProduct extends this {
        async $enter() {
          this.setLoadingAnimation(true)

          this._productsMap = {}
          this._lastState = this._getState()
          this.showProductsContainer()
          this._currentButton = this.passwordInputField

          try {
            // Get native platform offers
            AppConfig.shared.subscriptionService.initialize()
            this._nativeProducts = await AppConfig.shared.subscriptionService.getProducts()

            // Get FloSports offers and their details
            const products = await AppConfig.shared.authService.getOffers({
              ...this._contextData,
              currency: this._nativeProducts?.[0]?.currency ?? null,
              type: AppConfig.shared.subscriptionService.getType(),
            })

            const productsDetails = await Promise.all(
              products?.map((p) => AppConfig.shared.authService.getOffer({ id: p.id })),
            )

            // Native offers/products must match Flo offers
            this._commonProducts = products?.filter(({ id }) =>
              productsDetails?.some((p1) => {
                const p2 = this._nativeProducts.find((p) => p.id === p1.platformId)
                const match = p1.id === id && p2
                if (match) {
                  this._productsMap[id] = p2
                }

                return match
              }),
            )

            this.productsList.products = this._commonProducts
          } catch (e) {
            console.error(e)

            this.setModal(true, {
              title: getText('error'),
              info: getText('signUpGenericOffersError'),
              buttons: [
                {
                  label: getText('close'),
                  callback: () => {
                    if (this._fromSettings) {
                      Router.back()
                      return
                    }

                    if (isUserSignedIn()) {
                      Router.navigate(HOME)
                    } else {
                      Router.back()
                    }
                  },
                  isDefaultBackAction: true,
                },
              ],
            })
            Router.focusWidget('Modal')
          }

          this.setLoadingAnimation(false)
        }

        _getFocused(): Lightning.Element {
          return this.productsList._getFocused()
        }

        _handleBack() {
          if (this._isPurchasingSubscription) {
            // Prevent user from exiting screen until native checkout process window is shown to the user
            return
          }

          if (this._fromSettings) {
            Router.back()
            return
          }

          if (isUserSignedIn()) {
            Router.navigate(HOME)
          } else {
            Router.back()
          }
        }
      },

      class Ready extends this {
        $enter() {
          // State Ready is set by the Modal, so we need to restore the state accordingly
          this._setState(this._lastState)
        }
      },
    ]
  }

  getFirstFocusElement(): Lightning.Component {
    return this._currentButton ?? this.emailInputField
  }

  getFocusedElement(): Lightning.Component {
    return this._currentButton
  }

  focusFirstElement(): void {
    this._currentButton = this._currentButton ?? this.emailInputField
    this._setState(this._lastState)
  }

  isAtTopMostElement(): boolean {
    return false
  }

  showEmailContainer() {
    this.passwordContainer.visible = false
    this.emailContainer.visible = true
    this.productsContainer.visible = false
  }

  showPasswordContainer() {
    this.emailContainer.visible = false
    this.passwordContainer.visible = true
    this.productsContainer.visible = false
  }

  showProductsContainer() {
    this.emailContainer.visible = false
    this.passwordContainer.visible = false
    this.productsContainer.w = 1920
    this.productsContainer.visible = true
  }

  onEmailChange(value) {
    this._continueStep1BtnEnabled = validateEmail(value)
    this.continueStep1Button.isEnabled = this._continueStep1BtnEnabled
  }

  onEmailComplete() {
    if (validateEmail(this.emailInputField.getText())) {
      this._currentButton = this.continueStep1Button
    }
  }

  onPasswordComplete() {
    this._currentButton = this.continueStep2Button
  }

  async registerUser() {
    if (this._password?.length < 6) {
      this.setModal(true, {
        title: getText('error'),
        info: getText('signUpPasswordError'),
        buttons: [
          {
            label: getText('close'),
            isDefaultBackAction: true,
          },
        ],
      })
      Router.focusWidget('Modal')
      return
    }

    this.setLoadingAnimation(true)
    const res = await AppConfig.shared.authService.createUser({
      email: this._email,
      plain_password: this._password,
    })

    this.setLoadingAnimation(false)

    if (typeof res !== 'boolean') {
      this.setModal(true, {
        wordWrap: true,
        title: getText('error'),
        info: getText(res.error ?? 'signUpCreateAccountError'),
        buttons: [
          {
            label: getText('close'),
            isDefaultBackAction: true,
            callback: () => {
              if (res.error === 'signUpCreateAccountDuplicateEmailError') {
                this._password = null
                this.passwordInputField.setText('')
                this._currentButton = this.emailInputField
                this._setState(SignUpStates.EnterEmail)
              } else {
                this._setState(this._lastState)
              }
              Router.focusPage()
              this.widgets.modal.visible = false
            },
          },
        ],
      })
      Router.focusWidget('Modal')
      return
    }

    await this.doLogin()

    this._setState(SignUpStates.SelectProduct)
  }

  async doLogin() {
    this.setLoadingAnimation(true)
    const data = await AppConfig.shared.authService.login({
      email: this._email,
      password: this._password,
    })

    if ('error' in data && data.isNetworkError) {
      this.fireAncestors('$toggleLoading', false)
      this.fireAncestors('$connectionError', {
        title: getText('nonBffConnectionErrorTitle'),
        info: getText('connectionErrorSubtitle'),
        footer: getText('loginRemoteErrorFooter'),
        retryAction: () => {
          this._setState(this._lastState)
          Router.focusPage()
        },
      })
      return
    }

    const authToken = (data as Flo.Auth.LoginResponse)?.token
    const refreshToken = (data as Flo.Auth.LoginResponse)?.refresh_token

    if (!authToken || !refreshToken) {
      this.setLoadingAnimation(false)
      this.setModal(true, {
        wordWrap: true,
        title: getText('error'),
        info: getText('signUpLoginError'),
        buttons: [{ label: getText('close') }],
      })
      Router.focusWidget('Modal')
      return
    }

    storeUserData(data as Flo.Auth.LoginResponse)

    // Update user subscription data if needed
    await updateUserSubscriptionData()

    AppConfig.shared.appSettings.homeData = null

    if (isTizen) {
      await AppConfig.shared.samsungCWService.restoreContinueWatching()
    }

    this.setLoadingAnimation(false)

    void captureSessionData()
  }

  async $onSelectProduct(prodIndex: number) {
    if (this._commonProducts.length < 1) return

    this.setLoadingAnimation(true)
    this._isPurchasingSubscription = true
    this._subscriptionPurchaseTimeoutId = Registry.setTimeout(() => {
      this._isPurchasingSubscription = false
    }, SUBSCRIPTION_PURCHASE_TIMEOUT_DELAY)

    const product = this._commonProducts[prodIndex]
    const offer = await AppConfig.shared.authService.getOffer({ id: product.id })
    const nativeProduct = this._productsMap[offer.id]

    void captureClick(SEGMENT_TRACK_EVENTS.ButtonClicked, nativeProduct?.title, 'Buy Product')

    console.log(offer)
    console.log(nativeProduct)
    const res = await AppConfig.shared.subscriptionService.buyProduct(nativeProduct)
    console.log('result from buy product:', res)

    Registry.clearTimeout(this._subscriptionPurchaseTimeoutId)
    this._subscriptionPurchaseTimeoutId = null
    this._isPurchasingSubscription = false

    if (!res) {
      console.log('Error buying product')
      this.showErrorBuyProduct()
      return
    } else if (res.isPurchaseCanceled) {
      this.setLoadingAnimation(false)
      return
    }

    const subscriptionData = await AppConfig.shared.subscriptionService.createSubscription(
      offer,
      res.invoiceId,
    )

    console.log('result from create subscription:', subscriptionData)

    if (!subscriptionData || !subscriptionData.status) {
      console.log('Error creating subscription')
      this.showErrorBuyProduct()
      return
    }

    updateUserSubscriptionData(subscriptionData.subscription)

    this.setLoadingAnimation(false)

    console.log('Success - show success')
    this.setModal(true, {
      wordWrap: true,
      title: getText('success'),
      info: getText('signUpSuccessful'),
      buttons: [
        {
          label: getText('close'),
          callback: () => {
            if (hasActiveSubscription(subscriptionData.subscription)) {
              const history = Router.getHistory()
              let hash = history[history.length - 1]?.hash
              if (hash === FIRST_LANDING || hash === LOGIN) {
                Router.navigate(HOME)
              } else if (history.length > 2) {
                hash = history[history.length - 2]?.hash
                Router.go(hash.startsWith(BASE_ROUTES.PLAYER) ? -2 : -3)
              } else {
                Router.back()
              }

              Router.focusPage()
            }
          },
        },
      ],
    })
    Router.focusWidget('Modal')
  }

  $onFocusProduct(period: string) {
    if (period === 'yearly') {
      this.setProductInfo(getText('signUpProductDescriptionAnnual'))
    } else if (period === 'monthly') {
      this.setProductInfo(getText('signUpProductDescriptionMonthly'))
    }
  }

  showErrorBuyProduct() {
    this.setLoadingAnimation(false)
    this._lastState = this._getState()

    this.setModal(true, {
      wordWrap: true,
      title: getText('error'),
      info: getText('signUpGenericBuyError'),
      buttons: [
        {
          label: getText('close'),
          callback: () => {
            this._setState(this._lastState)
            this.setModal(false)
            Router.focusPage()
          },
          isDefaultBackAction: true,
        },
      ],
    })
    Router.focusWidget('Modal')
  }

  setProductInfo(info: string) {
    const text = info.split('|')
    if (text?.length > 0) {
      this.headerText = text[0]
      this.productInfoBullets.children = text.slice(1).map((l) => {
        return {
          type: ProductBullet,
          infoText: l,
        }
      })
    }
  }

  validateEmailString() {
    const isEmailView = this.emailContainer.visible
    const input = isEmailView ? this.emailInputField : this.passwordInputField
    let curText = input.getText()

    if (isEmailView) {
      this._continueStep1BtnEnabled = validateEmail(curText)
      this.continueStep1Button.isEnabled = this._continueStep1BtnEnabled
    }
  }

  $focusVizioKeyboard() {
    this._lastState = this.emailContainer.visible
      ? SignUpStates.EnterEmail
      : SignUpStates.EnterPassword
    this._setState(SignUpStates.Keyboard)
  }

  $unfocusVizioKeyboard() {
    this.validateEmailString()
    this._setState(this._lastState)
  }

  $onVizioKeyboardKeyPress(key: string) {
    const isEmailView = this.emailContainer.visible
    const input = isEmailView ? this.emailInputField : this.passwordInputField
    let curText = input.getText()

    switch (key) {
      case 'Done':
        this.validateEmailString()
        this._setState(this._lastState)
        return

      case 'Clear':
        input.setText('')
        return

      case 'Backspace':
        if (curText.length > 0) {
          input.setText(curText.slice(0, -1))
        } else {
          this.$unfocusVizioKeyboard()
        }
        return

      case 'Space':
        input.setText(curText + ' ')
        return

      default:
        input.setText(curText === '' ? key : curText + key)
    }
  }
}
